import { Box, Fade, Stack } from '@mui/material';
import { AnimatePresence } from 'motion/react';
import { FC, Fragment } from 'react';

import { VpFade } from '@vp/common/ui/component/VpFade';
import { VpMediaGrid } from '@vp/common/ui/component/VpMediaGrid';
import { useSearchSection } from '@vp/common/ui/hook/useSearchSection';
import { useSearchSectionCheck } from '@vp/common/ui/hook/useSearchSectionCheck';
import { useViewModel } from '@vp/common/ui/hook/useViewModel';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfileAccess } from '@vp/profile/ui/access/ProfileAccess';
import { GalleryTabs } from '@vp/profile/ui/gallery/GalleryTabs';
import { ProfileImageViewer } from '@vp/profile/ui/gallery/image/ProfileImageViewer';
import { ProfileGalleryMode, ProfileGalleryViewModel } from '@vp/profile/ui/gallery/ProfileGalleryViewModel';
import { ProfileVideoViewer } from '@vp/profile/ui/gallery/video/ProfileVideoViewer';
import { useSectionMinHeight } from '@vp/profile/ui/hook/useSectionMinHeight';

export interface ProfilePhotosProps {
  profile: ProfileModel;
}

export const ProfileGallery: FC<ProfilePhotosProps> = ({ profile }) => {
  const viewModel = useViewModel(ProfileGalleryViewModel, { profile });
  const minHeight = useSectionMinHeight();
  const mode = useSearchSection<ProfileGalleryMode>('m', ProfileGalleryMode.Images);

  useSearchSectionCheck('m', ProfileGalleryMode.Images);

  return (
    <Fragment>
      <Box px={{ mobile: 3, tablet: 7 }} pt={{ mobile: 2, tablet: 3 }} pb={{ mobile: 2, tablet: 4 }} sx={{ minHeight }}>
        <AnimatePresence mode="wait">
          <VpFade key={profile.access.canAccessGallery().toString()} duration={0.25}>
            {profile.access.canAccessGallery() && (
              <Fade key={mode} in appear timeout={250} easing="ease-in-out">
                <Box>
                  {mode === ProfileGalleryMode.Images && (
                    <VpMediaGrid clickable items={viewModel.photoItems.value} onClick={viewModel.handlePhotoClick} />
                  )}
                  {mode === ProfileGalleryMode.Videos && (
                    <VpMediaGrid clickable items={viewModel.videoItems.value} onClick={viewModel.handleVideoClick} />
                  )}
                </Box>
              </Fade>
            )}

            {!profile.access.canAccessGallery() && (
              <Stack width={1} alignItems="center" pt={{ tablet: 4, desktop: 16.875 }}>
                <ProfileAccess />
              </Stack>
            )}
          </VpFade>
        </AnimatePresence>
      </Box>

      <GalleryTabs shown={profile.access.canAccessGallery()} mode={mode} />

      <ProfileImageViewer
        open={viewModel.imageViewerShown.value}
        selectedPhotoId={viewModel.selectedPhoto.value?.id ?? ''}
        photos={profile.photos}
        close={viewModel.closeImageViewer}
      />

      <ProfileVideoViewer
        open={viewModel.videoViewerShown.value}
        video={viewModel.selectedVideo.value}
        close={viewModel.closeVideoViewer}
      />
    </Fragment>
  );
};
